import React, { useState, useEffect, useRef } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import API_CONFIG from '../../apiConfig';
import '../../styles/components/ForgotPasswordForm.css';

const ForgotPasswordForm = ({ t, onBack }) => {
  const [step, setStep] = useState('request'); // 'request' | 'verify' | 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRefs = useRef([]);

  // Send/Resend OTP Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(API_CONFIG.FORGOT_PASSWORD_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = JSON.parse(text);
      } catch (err) {}

      if (!response.ok) {
        throw new Error(data.message || t('send_otp_failed', 'Failed to send OTP. Please verify your email and try again.'));
      }

      setSuccess(data.message || t('otp_sent', 'An OTP code has been sent to your email.'));
      setResendCooldown(60);
      setTimeout(() => {
        setStep('verify');
        setSuccess(null);
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(API_CONFIG.FORGOT_PASSWORD_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = JSON.parse(text);
      } catch (err) {}

      if (!response.ok) {
        throw new Error(data.message || t('send_otp_failed', 'Failed to send OTP. Please verify your email and try again.'));
      }

      setSuccess(data.message || t('otp_sent', 'An OTP code has been sent to your email.'));
      setResendCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError(t('enter_valid_otp', 'Please enter a 6-digit OTP code.'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(API_CONFIG.VERIFY_RESET_OTP_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp: otpString }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = JSON.parse(text);
      } catch (err) {}

      console.log("Verify OTP full response data:", data);

      if (!response.ok) {
        throw new Error(data.message || t('invalid_otp', 'Verification code is invalid or expired.'));
      }

      setSuccess(data.message || t('verification_success', 'OTP verified successfully!'));
      setTimeout(() => {
        setStep('reset');
        setSuccess(null);
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError(t('password_min_validation', 'Password must be at least 6 characters.'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('password_not_match', 'Passwords do not match.'));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Align with Postman request: send email, password, and password_confirmation
      const response = await fetch(API_CONFIG.RESET_PASSWORD_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password,
          password_confirmation: confirmPassword,
        }),
      });

      const text = await response.text();
      let data = {};
      try {
        data = JSON.parse(text);
      } catch (err) {}

      console.log("Reset password response:", data);

      if (!response.ok) {
        throw new Error(data.message || t('server_error', 'An error occurred. Please try again.'));
      }

      setSuccess(data.message || t('password_reset_success', 'Password reset successfully!'));
      alert("Your password has been reset successfully!");
      onBack(); // Go back to login

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const num = value.replace(/[^0-9]/g, '');
    const newOtp = [...otp];
    newOtp[index] = num.substring(num.length - 1);
    setOtp(newOtp);

    if (newOtp[index] && index < 5) {
      otpInputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (!newOtp[index] && index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        otpInputRefs.current[index - 1].focus();
      } else {
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pasteData)) {
      const digits = pasteData.split('');
      setOtp(digits);
      otpInputRefs.current[5].focus();
    }
  };

  return (
    <div className="auth-column forgot-password-column">
      {step === 'request' && (
        <div className="form-container">
          <span className="title">{t('forgot_password_title', 'Forgot Password')}</span>
          <p className="description">
            {t('forgot_password_desc', 'Enter your registered email address below, and we will send you a 6-digit verification code to reset your password.')}
          </p>

          {error && (
            <div className="alert-box error">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="alert-box success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSendOtp} className="forgot-password-form">
            <div className="form-group">
              <input
                type="email"
                id="forgot-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('email_address', 'Email Address')}
                required
                disabled={loading}
              />
              <label htmlFor="forgot-email">{t('email_address', 'Email Address')}</label>
            </div>

            <div className="buttons-group">
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? t('sending', 'Sending...') : t('next', 'Next')}
                {!loading && <FaArrowRight />}
              </button>
              <button type="button" className="btn-back" onClick={onBack} disabled={loading}>
                {t('back_to_auth', 'Back')}
              </button>
            </div>
          </form>
        </div>
      )}

      {step === 'verify' && (
        <div className="form-container">
          <span className="title">{t('verify_account', 'Verify Code')}</span>
          <p className="description">
            {t('verify_prompt_1', 'Please enter the 6-digit verification code sent to')} <strong style={{ color: '#2563eb' }}>{email}</strong>.
          </p>

          {error && (
            <div className="alert-box error">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="alert-box success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleVerifyOtp}>
            <div className="otp-inputs-row">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  ref={(el) => (otpInputRefs.current[index] = el)}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  className="otp-input-field"
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <div className="resend-container">
              {resendCooldown > 0 ? (
                <span>
                  {t('resend_in', 'Resend code in')} <strong style={{ color: '#2563eb' }}>{resendCooldown}s</strong>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading}
                  className="btn-resend-otp"
                >
                  {t('resend_code', 'Resend verification code')}
                </button>
              )}
            </div>

            <div className="buttons-group">
              <button type="submit" className="btn-submit" disabled={otp.some(d => d === '') || loading}>
                {loading ? t('verifying', 'Verifying...') : t('verify_now', 'Verify Code')}
                {!loading && <FaArrowRight />}
              </button>
              <button type="button" className="btn-back" onClick={() => setStep('request')} disabled={loading}>
                {t('back_to_auth', 'Back')}
              </button>
            </div>
          </form>
        </div>
      )}

      {step === 'reset' && (
        <div className="form-container">
          <span className="title">{t('create_password', 'Create Password')}</span>
          <p className="description">
            {t('reset_password_desc', 'Please enter your new secure password below to complete the reset process.')}
          </p>

          {error && (
            <div className="alert-box error">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="alert-box success">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <input
                type="password"
                id="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('password', 'Password')}
                required
                disabled={loading}
              />
              <label htmlFor="new-password">{t('password', 'Password')}</label>
            </div>

            <div className="form-group">
              <input
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('confirm_password', 'Confirm Password')}
                required
                disabled={loading}
              />
              <label htmlFor="confirm-password">{t('confirm_password', 'Confirm Password')}</label>
            </div>

            <div className="buttons-group">
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? t('saving', 'Saving...') : t('submit', 'Submit')}
                {!loading && <FaArrowRight />}
              </button>
              <button type="button" className="btn-back" onClick={() => setStep('verify')} disabled={loading}>
                {t('back_to_auth', 'Back')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ForgotPasswordForm;
